import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSPage
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";
import { queriesPayeeRows } from "@query";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { useUrlQueryFilters } from "../../../../utils/router";
import { SearchPanel } from "../../../Invoicing/components/SearchPanel";
import { TablePayees } from "../../components/TablePayees/TablePayees";
import { PAYEES_ROOT_PATH } from "../../utils";

/**
 * The URL-persisted state for this screen: just the search box. Kept as a flat
 * string rather than the v3 `{ eq }` filter shape so existing `?searchString=…`
 * links keep working — it is widened into the SDK filter at the query below.
 */
type PayeesSearchFilters = {
  searchString?: string;
};

const DEFAULT_FILTERS: PayeesSearchFilters = {};

export const RoutePayeesSearch: React.FC = () => {
  const history = useHistory();
  const xWingspanAccount = useXWingspanExpansion();

  const { filters, setFilters } =
    useUrlQueryFilters<PayeesSearchFilters>(DEFAULT_FILTERS);

  const searchString = filters.searchString || "";

  // v3 `searchPayeeRows`, the same read the contractors list uses — was v1
  // `useQueryPayeeRows` + `useQueryPayeeRowsSummary`, whose search service
  // throws `NotMigrated`, so this screen returned nothing at all.
  const queryPayeeRows = queriesPayeeRows.searchPayeeRows.usePaginatedQuery(
    {
      filter: { searchString: { eq: searchString } },
      xWingspanAccount
    },
    { enabled: !!searchString }
  );

  const payees = queryPayeeRows.flatData ?? [];
  // v1 read the count from a second summary request; v3 returns it on the first
  // page of the same query (there is no row-summary endpoint on v3).
  const totalCount =
    queryPayeeRows.data?.pages?.[0]?.pagination?.totalSize ?? 0;

  return (
    <WSPage>
      <SearchPanel
        mb="L"
        context="payees"
        onBack={() => {
          history.push(PAYEES_ROOT_PATH);
        }}
        value={searchString}
        onSearch={newValue => {
          setFilters({ searchString: newValue });
        }}
        onClear={() => {
          setFilters({});
        }}
        placeholder="Search contractors"
      />

      {searchString ? (
        !queryPayeeRows.isInitialLoading && payees.length === 0 ? (
          <WSEmptyState
            py="3XL"
            hasBorder
            bgColor="gray"
            type="search"
            title="No results found"
            description="Try adjusting your search or filter to find what you’re looking for."
          />
        ) : (
          <>
            <WSFilters
              my="L"
              values={{}}
              onFilter={() => {}}
              info={{ count: totalCount }}
            />
            <WSInfiniteScroll
              mt="M"
              onLoad={() => {
                queryPayeeRows.fetchNextPage();
              }}
              loadMore={payees.length > 0}
              endOfList={!queryPayeeRows.hasNextPage}
              loading={!!queryPayeeRows.isFetchingNextPage}
            >
              <TablePayees
                payees={payees}
                loading={queryPayeeRows.isInitialLoading}
              />
            </WSInfiniteScroll>
          </>
        )
      ) : null}
    </WSPage>
  );
};
